import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {TrainingPlanEditor} from './ui/TrainingPlanEditor';
import {Block} from './models/Block';
import {v4} from 'uuid';
import {initialize} from './component';
import {EventType, PartEvent} from './models/PartEvent';
import {Anchor, AnchorOffsetType, AnchorPoint} from './models/Anchor';
import {Expander} from './business/expanded_parts/Expander';
import {MediaItem} from './models/MediaItem';
import {Chapter} from './models/Chapter';
import {DeviceName} from './models/DeviceName';

const elements = Array.from(document.getElementsByClassName('training-plan-editor'));

elements.forEach((element) => {
    const data = element.getAttribute('data-training-plan');
    if (data) {
        const trainingPlan: {
            id: number;
            title: string;
            description: string | null;
            device_requirement_rules: Array<{
                requires_all: boolean;
                devices: DeviceName[];
            }>;
            is_published: boolean;
            blocks: Block[];
            events: Array<{
                part_number: number;
                type: EventType;
                key: any;
                value: any;
                anchor_point: AnchorPoint;
                anchor_offset_type: AnchorOffsetType;
                anchor_offset_value: number;
            }>;
            chapters: Array<{
                part_index: number;
                title: string;
                thumbnail_id: number | null;
                thumbnail_url: string | null;
            }>;
            media: Array<{
                media_id: number | null;
                part_index: number;
                playbackrate: number;
                anchor_point: AnchorPoint;
                anchor_offset_type: AnchorOffsetType;
                anchor_offset_value: number;
            }>;
        } = JSON.parse(data);

        const initialBlocks = trainingPlan.blocks.map((block) => {
            return {
                ...block,
                __uuid: v4(),
                parts: block.parts.map((part) => {
                    return {
                        ...part,
                        __uuid: v4(),
                    };
                }),
            };
        });

        const expandedParts = new Expander().expand(initialBlocks);

        const initialEvents: PartEvent[] = trainingPlan.events
            .filter((event) => event.part_number > 0 && event.part_number <= expandedParts.length)
            .map((event) => {
                const anchor: Anchor = {
                    __uuid: v4(),
                    __expandedPartUuid: expandedParts[event.part_number - 1].__uuid,
                    anchor_point: event.anchor_point,
                    offset_type: event.anchor_offset_type,
                    offset_value: event.anchor_offset_value,
                };

                switch (event.type) {
                    case EventType.Target:
                        return {
                            __uuid: v4(),
                            type: EventType.Target,
                            key: event.key,
                            value: event.value,
                            anchor: anchor,
                        };
                    case EventType.DataField0:
                    case EventType.DataField1:
                    case EventType.DataField2:
                    case EventType.DataField3:
                        return {
                            __uuid: v4(),
                            type: event.type,
                            key: event.key,
                            value: event.value,
                            anchor: anchor,
                        };
                    case EventType.Instruction:
                        return {
                            __uuid: v4(),
                            type: EventType.Instruction,
                            key: event.key,
                            value: event.value,
                            anchor: anchor,
                        };
                    case EventType.MainVisualisation:
                        return {
                            __uuid: v4(),
                            type: EventType.MainVisualisation,
                            key: event.key,
                            value: event.value,
                            anchor: anchor,
                        };
                }
            });

        const initialMediaItems: MediaItem[] = trainingPlan.media
            .map((mediaItem) => {
                if (!expandedParts[mediaItem.part_index]) {
                    return null;
                }
                const anchor: Anchor = {
                    __uuid: v4(),
                    __expandedPartUuid: expandedParts[mediaItem.part_index].__uuid,
                    anchor_point: mediaItem.anchor_point,
                    offset_type: mediaItem.anchor_offset_type,
                    offset_value: mediaItem.anchor_offset_value,
                };
                return {
                    __uuid: v4(),
                    anchor: anchor,
                    playbackrate: mediaItem.playbackrate,
                    mediaId: mediaItem.media_id!,
                };
            })
            .filter((item) => item !== null) as MediaItem[];

        const initialChapters = trainingPlan.chapters
            .map((chapter): Chapter | null => {
                if (!expandedParts[chapter.part_index]) {
                    return null;
                }
                return {
                    __uuid: v4(),
                    __expandedPartUuid: expandedParts[chapter.part_index].__uuid,
                    title: chapter.title,
                    thumbnail_id: chapter.thumbnail_id,
                    thumbnail_url: chapter.thumbnail_url,
                };
            })
            .filter((item) => item !== null) as Chapter[];

        const collectionId = element.getAttribute('data-collection-id');

        initialize(
            null,
            collectionId === null ? null : parseInt(collectionId, 10),
            initialBlocks,
            initialEvents,
            initialChapters,
            initialMediaItems,
        );

        ReactDOM.render(
            <TrainingPlanEditor
                id={trainingPlan.id}
                title={trainingPlan.title}
                description={trainingPlan.description}
                deviceRequirementRules={trainingPlan.device_requirement_rules}
                isPublished={trainingPlan.is_published}
                canEditOutline={!trainingPlan.is_published}
            />,
            element,
        );
    } else {
        const organisationId = element.getAttribute('data-organisation-id');
        const collectionId = element.getAttribute('data-collection-id');

        initialize(
            organisationId === null ? null : parseInt(organisationId, 10),
            collectionId === null ? null : parseInt(collectionId, 10),
            [],
            [],
            [],
            [],
        );
        ReactDOM.render(<TrainingPlanEditor canEditOutline={true} isPublished={false} />, element);
    }
});
